import React from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import moment from 'moment-timezone';
import { useUsers } from '@/Recoil/User';
import { FastAvatar } from '@/Components/FastAvatar';
import type { Post } from '@/Data/Social';
import './styles/PostReply.scss';

interface PostReplyProps {
    reply: Post;
    onDelete: (postid: string) => void;
    onReport: (postid: string) => void;
    onReply: (addressid: string) => void;
}

const PostReply: React.FC<PostReplyProps> = ({ reply, onDelete, onReport, onReply }) => {
    const { profiles } = useUsers();
    const currentUser = localStorage.getItem('addressid');
    const isOwner = reply.addressid === currentUser;
    
    const userProfile = profiles[reply.addressid];
    
    const [showMenu, setShowMenu] = React.useState(false);
    
    const formatTime = (timestamp: number) => {
        const now = moment();
        const postTime = moment(timestamp * 1000);
        const diffHours = now.diff(postTime, 'hours');
        const diffDays = now.diff(postTime, 'days');
        
        if (diffHours < 1) return 'now';
        if (diffHours < 24) return `${diffHours}h`;
        return `${diffDays}day`;
    };
    
    return (
        <div className="PostReply">
            <div className="View ReplyContainer">
            <div className="View ReplyContent">
                <FastAvatar
                    uri={userProfile?.image}
                    userid={reply.addressid}
                    size={32}
                    style={{ borderRadius: '50%' }}
                />
                
                <div className="View ReplyMain">
                    <div className="View ReplyHeader">
                        <div className="View UserInfo">
                            <span className="Text Username">@{reply.addressid}</span>
                            <span className="Text Timestamp">{formatTime(reply.created_at || 0)}</span>
                        </div>
                        
                        <div className="View MoreButtonContainer">
                            <button 
                                className="Pressable MoreButton"
                                onClick={() => setShowMenu(!showMenu)}
                            >
                                <MoreHorizIcon />
                            </button>
                            
                            {showMenu && (
                                <>
                                    <div 
                                        className="MenuOverlay" 
                                        onClick={() => setShowMenu(false)}
                                    />
                                    <div className="View MenuDropdown">
                                        {isOwner ? (
                                            <button 
                                                className="Pressable MenuItem"
                                                onClick={() => {
                                                    onDelete(reply.postid);
                                                    setShowMenu(false);
                                                }}
                                            >
                                                <span className="Text MenuText">Delete</span>
                                            </button>
                                        ) : (
                                            <>
                                                <button 
                                                    className="Pressable MenuItem"
                                                    onClick={() => {
                                                        onReply(reply.addressid);
                                                        setShowMenu(false);
                                                    }}
                                                >
                                                    <span className="Text MenuText">Reply</span>
                                                </button>
                                                <button 
                                                    className="Pressable MenuItem"
                                                    onClick={() => {
                                                        onReport(reply.postid);
                                                        setShowMenu(false);
                                                    }}
                                                >
                                                    <span className="Text MenuText">Report</span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    
                    <div className="Text ReplyText">
                        {reply.content}
                    </div>
                    
                    {reply.media && reply.media.length > 0 && (
                        <div className="View MediaContainer">
                            {reply.media.map((url, index) => (
                                <img 
                                    key={index} 
                                    src={url} 
                                    alt="" 
                                    className="FastImage MediaImage"
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
        </div>
    );
};

export default PostReply;